import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  const loadCart = async () => {
    try {
      const res = await fetch(`${BASE_URL}/enduser/cart/`, {
        credentials: "include",
      });

      if (!res.ok) {
        setCartItems([]);
        return;
      }

      const data = await res.json();
      setCartItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load cart", err);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (isAuthenticated) {
      loadCart();
    } else {
      setCartItems([]);
      setLoading(false);
    }
  }, [isAuthenticated]);

  const updateCart = async ({ type, id, delta }) => {
    try {
      await fetch(`${BASE_URL}/enduser/cart/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          id,
          delta,
        }),
      });


      await loadCart();
    } catch (err) {
      console.error("Failed to sync cart", err);
    }
  };

  const clearCart = async () => {
    setCartItems([]);

    try {
      await fetch(`${BASE_URL}/enduser/cart/`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch (err) {
      console.error("Failed to clear cart", err);
    }
  };

  const getCartItemQuantity = (type, matcher) => {
    if (!Array.isArray(cartItems)) return 0;

    const item = cartItems.find(
      (entry) => entry.type === type && matcher(entry)
    );

    return item?.quantity ?? 0;
  };

  const getProductQuantity = (productId) => {
    return getCartItemQuantity(
      "product",
      (item) => item.product?.id === productId
    );
  };

  const getComboQuantity = (comboId) => {
    return getCartItemQuantity(
      "combo",
      (item) => item.combo?.id === comboId
    );
  };

  const getClearanceQuantity = (clearanceId) =>
    getCartItemQuantity(
      "clearance",
      (item) =>
        item.clearance_product?.id === clearanceId ||
        item.product?.id === clearanceId
    );

  const getPreorderQuantity = (preorderId) =>
    getCartItemQuantity(
      "preorder",
      (item) => item.preorder_product?.id === preorderId
    );

  const isProductInCart = (productId) =>
    getProductQuantity(productId) > 0;

  const isComboInCart = (comboId) =>
    getComboQuantity(comboId) > 0;

  const isClearanceInCart = (clearanceId) =>
    getClearanceQuantity(clearanceId) > 0;

  const isPreorderInCart = (preorderId) =>
    getPreorderQuantity(preorderId) > 0;

  const cartCount = Array.isArray(cartItems)
    ? cartItems.length
    : 0;


  return (
    <CartContext.Provider
      value={{
        cartItems,
        loading,


        updateCart,
        clearCart,
        reloadCart: loadCart,


        getProductQuantity,
        getComboQuantity,
        getClearanceQuantity,
        getPreorderQuantity,
        isProductInCart,
        isComboInCart,
        isClearanceInCart,
        isPreorderInCart,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return ctx;
};
